import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  ViewChild,
} from '@angular/core';
import { TableBaseAbstractComponent } from '../../../shared/components/table-base-abstract.component';
import { Table, TableModule } from 'primeng/table';
import { TableRequestData } from '../../../shared/models/table/table-request-param';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  map,
  Observable,
  tap,
} from 'rxjs';
import { TableResponse } from '../../../shared/models/table-response';
import { Announcement } from '../../../shared/models/announcement';
import { AnnouncementsApi } from '../../../shared/services/api/announcements.api';
import { SortDirection } from '../../../shared/enums/sort-direction.enum';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import {
  ANNOUNCEMENT_STATUS_LABELS,
  AnnouncementStatus,
} from '../../../shared/enums/announcement-status.enum';
import { DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Card } from 'primeng/card';
import { Paginator } from 'primeng/paginator';
import { PreviewDataWrapperComponent } from '../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { PreviewDataType } from '../../../shared/enums/preview-data-type.enum';
import { TooltipModule } from 'primeng/tooltip';
import { DatePicker } from 'primeng/datepicker';
import { InputText } from 'primeng/inputtext';
import { AppIconComponent } from '../../../shared/components/app-icon/app-icon.component';
import {
  ANNOUNCEMENT_CONFIRMATION_REQUIREMENT_LABELS,
  AnnouncementConfirmationRequirement,
} from '../../../shared/enums/announcement-confirmation-requirement.enum';
import { AnnouncementChannel } from '../../../shared/enums/announcement-channel.enum';
import { PrimeFilterTarget } from '../../../shared/models/custom-types';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { ToastSeverity } from '../../../shared/enums/toast-severity.enum';
import { Router } from '@angular/router';
import { APP_PATHS } from '../../../core/config/app-paths';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ANNOUNCEMENT_CHANNEL_OPTIONS,
  ANNOUNCEMENT_RECIPIENT_TYPE_OPTIONS,
  ANNOUNCEMENT_STATUS_OPTIONS,
} from '../../../shared/constants/announcement-form-options';
import {
  ANNOUNCEMENT_RECIPIENT_TYPE_LABELS,
  AnnouncementRecipientType,
} from '../../../shared/enums/announcement-recipient-type.enum';
import { SpinnerService } from '../../../core/services/spinner.service';
import { HttpResponseService } from '../../../core/services/http-error-response.service';
import { DokumentApi } from '../../../shared/services/api/dokument.api';
import { IsAnnouncementDeleteAvailablePipe } from './pipe/is-announcement-delete-available.pipe';
import { AnnouncementStatusBadgeClassPipe } from './pipe/announcement-status-badge-class.pipe';

@Component({
  selector: 'app-admin-panel-announcements',
  imports: [
    AppIconComponent,
    Button,
    Card,
    DatePicker,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    Paginator,
    PreviewDataWrapperComponent,
    Select,
    TableModule,
    TooltipModule,
    IsAnnouncementDeleteAvailablePipe,
    AnnouncementStatusBadgeClassPipe,
  ],
  templateUrl: './admin-panel-announcements.html',
  styleUrl: './admin-panel-announcements.scss',
  providers: [AnnouncementsApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelAnnouncements extends TableBaseAbstractComponent<Announcement> {
  @ViewChild('table') override tableRef!: Table;
  protected readonly PreviewDataType = PreviewDataType;
  protected readonly AnnouncementStatus = AnnouncementStatus;
  protected readonly confirmationOptions = [
    {
      label:
        ANNOUNCEMENT_CONFIRMATION_REQUIREMENT_LABELS[
          AnnouncementConfirmationRequirement.TAK
        ],
      value: AnnouncementConfirmationRequirement.TAK,
    },
    {
      label:
        ANNOUNCEMENT_CONFIRMATION_REQUIREMENT_LABELS[
          AnnouncementConfirmationRequirement.NIE
        ],
      value: AnnouncementConfirmationRequirement.NIE,
    },
    {
      label:
        ANNOUNCEMENT_CONFIRMATION_REQUIREMENT_LABELS[
          AnnouncementConfirmationRequirement.NIE_DOTYCZY
        ],
      value: AnnouncementConfirmationRequirement.NIE_DOTYCZY,
    },
  ];
  protected readonly channelOptions = ANNOUNCEMENT_CHANNEL_OPTIONS;
  protected readonly statusOptions = ANNOUNCEMENT_STATUS_OPTIONS;
  protected readonly recipientTypeOptions = ANNOUNCEMENT_RECIPIENT_TYPE_OPTIONS;
  protected tableFilters = {
    id: null as number | null,
    title: '',
    status: null as AnnouncementStatus | null,
    createdAt: null as Date | null,
    startDate: null as Date | null,
    endDate: null as Date | null,
    recipientType: null as AnnouncementRecipientType | null,
    createdBy: '',
    content: null as string | null,
    requiresConfirmation: null as AnnouncementConfirmationRequirement | null,
    requestCode: null as string | null,
    channel: null as AnnouncementChannel | null,
  };
  readonly #destroyRef = inject(DestroyRef);
  readonly #announcementsApi: AnnouncementsApi;
  readonly #dokumentApi = inject(DokumentApi);
  protected readonly requestCodeOptions = toSignal(
    this.#dokumentApi.getDokumentTypesCodes().pipe(
      map((data) =>
        data.map((value) => ({
          label: value,
          value: value,
        })),
      ),
      catchError((err) => {
        this.#httpResponseService.showErrorMessage(
          err,
          'Wystąpił błąd podczas pobierania niezbędnych danych do wypełnienia formularza',
        );
        return EMPTY;
      }),
    ),
    { initialValue: [] },
  );
  readonly #messageService = inject(MessageService);
  readonly #router = inject(Router);
  readonly #dialogService = inject(DialogService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #httpResponseService = inject(HttpResponseService);

  constructor(announcementsApi: AnnouncementsApi) {
    super();
    this.#announcementsApi = announcementsApi;
  }

  override onPrimeFilterChange(target: PrimeFilterTarget, nazwa: string): void {
    super.onPrimeFilterChange(target, nazwa);
  }

  override onPrimeFilterChangeMobile(
    target: PrimeFilterTarget,
    nazwa: string,
  ): void {
    super.onPrimeFilterChangeMobile(target, nazwa);
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<Announcement>> => {
    params.sortBy = params.sortBy ?? 'createdAt';
    params.order = params.order ?? SortDirection.DESC;
    return this.#announcementsApi.getAnnouncements$(params);
  };

  protected showDetails(announcement: Announcement): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}/${announcement.id}/${APP_PATHS.admin.details}`,
    ]);
  }

  protected openDeleteAnnouncementModal(announcement: Announcement): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: 'Czy na pewno chcesz usunąć komunikat?',
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Tak',
      },
    });

    ref?.onClose
      .pipe(
        filter(Boolean),
        tap(() => this.#deleteAnnouncement(announcement.id)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected addAnnouncement(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.announcements}/${APP_PATHS.admin.add}`,
    ]);
  }

  protected clearFilters(): void {
    this.tableFilters = {
      id: null,
      title: '',
      status: null,
      createdAt: null,
      startDate: null,
      endDate: null,
      recipientType: null,
      createdBy: '',
      content: null,
      requiresConfirmation: null,
      requestCode: null,
      channel: null,
    };
    this.resetFilters();
  }

  protected searchAnnouncements(_event?: Event): void {
    this.tableRequest.update((tableRequestData) => ({
      ...tableRequestData,
      pageIndex: 0,
      filters: {
        ...tableRequestData.filters,
        title: this.tableFilters.title,
        requiresConfirmation: this.tableFilters.requiresConfirmation,
        requestCode: this.tableFilters.requestCode,
        channel: this.tableFilters.channel,
      },
    }));
  }

  protected statusLabel(status: AnnouncementStatus): string {
    return ANNOUNCEMENT_STATUS_LABELS[status] ?? '-';
  }

  protected recipientTypeLabel(type: AnnouncementRecipientType): string {
    return ANNOUNCEMENT_RECIPIENT_TYPE_LABELS[type] ?? '-';
  }

  #deleteAnnouncement(announcementId: number): void {
    this.#spinnerService.show();
    this.#announcementsApi
      .deleteAnnouncement$(announcementId)
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie usunięto komunikat.',
          );
          this.reloadData();
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby usunięcia komunikatu',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #notifyTitleTooShort(): void {
    this.#messageService.add({
      key: 'admin',
      severity: ToastSeverity.ERROR,
      summary: 'Nieprawidłowy filtr',
      detail: 'Tytuł musi zawierać co najmniej 3 znaki.',
      life: 4000,
    });
  }
}
